import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Crown, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { UsagePill } from "@/components/UsagePill";
import { api, errMsg } from "@/lib/api";

const loadCheckout = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = resolve;
  s.onerror = () => reject(new Error("Couldn't load the payment window."));
  document.body.appendChild(s);
});

const Perk = ({ children }) => <li className="flex gap-2 text-sm"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{children}</li>;

export default function Upgrade() {
  const [plans, setPlans] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/plans"), api.get("/usage")]).then(([p, u]) => { setPlans(p.data); setUsage(u.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const pay = async () => {
    setBusy(true);
    try {
      await loadCheckout();
      const { data: o } = await api.post("/payments/razorpay/order");
      const rz = new window.Razorpay({
        key: o.key_id, order_id: o.order_id, amount: o.amount, currency: o.currency, name: o.name, description: o.description,
        prefill: { email: o.email }, theme: { color: "#ec4899" },
        handler: async (res) => {
          try {
            const { data } = await api.post("/payments/razorpay/verify", res);
            setUsage(data.usage);
            toast.success("You're Premium now! Enjoy up to 100 replies a day.");
          } catch (e) {
            toast.error(errMsg(e, "We couldn't confirm the payment yet. It will update automatically."));
          } finally {
            setBusy(false);
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rz.on("payment.failed", () => { toast.error("Payment failed. No Premium was charged."); setBusy(false); });
      rz.open();
    } catch (e) {
      toast.error(errMsg(e, e.message));
      setBusy(false);
    }
  };

  if (!plans || !usage) return <div className="min-h-screen"><Header /><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></div>;
  const premium = usage.plan === "premium";
  const enabled = plans.providers.razorpay;

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-8" data-testid="upgrade-page">
        <div className="space-y-3">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Get more rizz</h1>
          <p className="text-sm text-muted-foreground">Free gives you {plans.free.daily_limit} AI replies a day. Premium unlocks up to {plans.premium.daily_limit}. Counts reset at {plans.reset}.</p>
          <UsagePill usage={usage} />
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <section className="panel p-6 space-y-4" data-testid="plan-free">
            <div className="flex items-center gap-2 font-bold"><Zap className="h-5 w-5" />Free</div>
            <div className="text-3xl font-extrabold">₹0</div>
            <ul className="space-y-2"><Perk>{plans.free.daily_limit} AI replies or photo openers per day</Perk><Perk>Screenshot reading, Rizz Mode, every language</Perk></ul>
            {!premium && <div className="text-xs text-muted-foreground" data-testid="plan-free-current">Your current plan</div>}
          </section>
          <section className="panel p-6 space-y-4 ring-2 ring-primary/60" data-testid="plan-premium">
            <div className="flex items-center gap-2 font-bold"><Crown className="h-5 w-5 text-amber-400" />Premium</div>
            <div className="text-3xl font-extrabold">₹{plans.premium.price_inr} <span className="text-sm font-medium text-muted-foreground">/ {plans.premium.days} days</span></div>
            <ul className="space-y-2"><Perk>Up to {plans.premium.daily_limit} replies & photo openers per day</Perk><Perk>Everything in Free</Perk><Perk>One-time payment, no auto-renew</Perk></ul>
            {premium ? (
              <div className="text-sm font-semibold text-primary" data-testid="premium-active">Active until {new Date(usage.premium_until).toLocaleDateString()}{" "}
                <button data-testid="premium-extend-button" onClick={pay} disabled={!enabled || busy} className="ml-2 underline disabled:opacity-40">Extend</button></div>
            ) : (
              <button data-testid="premium-buy-button" onClick={pay} disabled={!enabled || busy}
                className="w-full h-12 rounded-full brand-gradient text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crown className="h-4 w-4" />}
                {enabled ? `Pay ₹${plans.premium.price_inr} with UPI / Card` : "Payments coming soon"}
              </button>
            )}
            {!enabled && <p className="text-xs text-muted-foreground" data-testid="payments-disabled-note">Online payments aren't switched on yet.</p>}
          </section>
        </div>
        <Link to="/app" data-testid="upgrade-back-link" className="text-sm text-primary">Back to the generator</Link>
      </main>
    </div>
  );
}
